import { useQuery } from "@tanstack/react-query";
import { type PerformanceWithAccountFilter } from "features/Library/Common/utils/performanceUtils";
import { getThreatDefensePerformanceTable } from "../../queries/threatDefensePerformanceQueries";
import { type ColumnOption } from "interfaces/columnOptions";
import { useEffect, useMemo } from "react";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { type ThreatDefenseReportTable } from "../types";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import styles from "./SendsTable.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type IconDefinition, faBan, faCalendarDays, faCircleCheck, faClock } from "@fortawesome/pro-solid-svg-icons";
import { type Dispatch } from "redux";
import { bindAction } from "interfaces";
import { setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { connect, type ConnectedProps } from "react-redux";
import { type RootState } from "features/Application/globaltypes/redux";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

type Props = PropsFromRedux & {
  filter: PerformanceWithAccountFilter;
  campaignId: number;
  handleSendSelection: (session: string, campaignRunId: number) => void;
};

enum Icons {
  Scheduled = "Scheduled",
  InProgress = "In Progress",
  Completed = "Completed",
  Canceled = "Canceled",
}

const StatusToIcon: { [key in Icons]: IconDefinition } = {
  [Icons.Scheduled]: faCalendarDays,
  [Icons.InProgress]: faClock,
  [Icons.Completed]: faCircleCheck,
  [Icons.Canceled]: faBan,
};

export const SendsTable = ({ filter, handleSendSelection, campaignId, setHasExportData }: Props) => {
  const formatDateTime = useReportingFormatWithTimeZone();
  const columns = useMemo<ColumnOption<ThreatDefenseReportTable>[]>(
    () => [
      {
        name: "Session",
        width: 5,
        isSortable: false,
        render(item) {
          const formattedDate = formatDateTime(item.CampaignRunStart);
          return (
            <Tooltip
              target={
                <TextTruncate
                  className={styles.invisibleButton}
                  onClick={() => handleSendSelection(formattedDate, item.CampaignRunId)}
                >
                  {formattedDate}
                </TextTruncate>
              }
              content={formattedDate}
            />
          );
        },
      },
      {
        name: "Sends",
        width: 4,
        isSortable: false,
        render(item) {
          return <div>{item.EmailsSent.toLocaleString()}</div>;
        },
      },
      {
        name: "Opens",
        isSortable: false,
        width: 4,
        render(item) {
          return (
            <div>
              <span>{item.EmailsOpened.toLocaleString()} </span>
              <span className={styles.percentage}>
                {item.EmailsSent > 0 ? `(${Math.round((item.EmailsOpened / item.EmailsSent) * 100)}%)` : "(0%)"}
              </span>
            </div>
          );
        },
      },
      {
        width: 4,
        name: "Clicks",
        isSortable: false,
        render(item) {
          return (
            <div>
              <span>{item.EmailsClicked.toLocaleString()} </span>
              <span className={styles.percentage}>
                {item.EmailsSent > 0 ? `(${Math.round((item.EmailsClicked / item.EmailsSent) * 100)}%)` : "(0%)"}
              </span>
            </div>
          );
        },
      },
      {
        width: 4,
        name: "Status",
        isSortable: false,
        render(item) {
          return (
            <div className={styles.icon}>
              <FontAwesomeIcon icon={StatusToIcon[item.Status]} className={styles[`${StatusToIcon[item.Status]}`]} />
              {item.Status}
            </div>
          );
        },
      },
    ],
    [handleSendSelection, formatDateTime],
  );

  const sendsQuery = useQuery({
    queryFn: getThreatDefensePerformanceTable,
    queryKey: ["threatDefensePerformanceTable", filter, campaignId],
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: sendsQuery.isSuccess && sendsQuery.data.length > 0,
      tooltipContent: "No data available for export. This campaign contains no data.",
    });
  }, [setHasExportData, sendsQuery]);

  return (
    <RequestStatusRenderer state={sendsQuery.status}>
      {sendsQuery.isSuccess && <GenericPerformanceList columns={columns} rows={sendsQuery.data} filter={filter} />}
    </RequestStatusRenderer>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    accountId: state.userProfile.accountId,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setHasExportData: bindAction(setHasDataToExport, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(SendsTable);
export default ConnectedComponent;
